import { useState } from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import { Box, Button, FormHelperText, TextField } from "@material-ui/core";
import toast from "react-hot-toast";
import LoadingButton from "../general/LoadingButton";
import { ValidationException } from "../../customExceptions";
import { useVerifyTwoFactor } from "../../mutations/auth";

const TwoFactorVerify = ({ challengeToken, onVerified, allowRecoveryCode }) => {
  // States

  const [useRecoveryCode, setUseRecoveryCode] = useState(false);

  // Mutations

  const { isPending: isVerifying, mutate: mutateVerify } = useVerifyTwoFactor();

  // Formik configuration

  const initialValues = { code: "", submit: null };

  const validationSchema = Yup.object().shape({
    code: useRecoveryCode
      ? Yup.string().max(20).required("Recovery code is required")
      : Yup.string()
          .matches(/^\d{6}$/, "Code must be 6 digits")
          .required("Code is required"),
  });

  const onSubmit = (values, { setErrors, setStatus, setSubmitting }) => {
    mutateVerify(
      { challengeToken, code: values.code },
      {
        onError: (error) => {
          toast.error("Verification failed");
          if (error instanceof ValidationException) {
            setStatus({ success: false });
            setErrors(error.validationErrors);
            setSubmitting(false);
          }
        },
        onSuccess: (result) => onVerified(result),
      }
    );
  };

  // Rendering

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
        dirty,
        isValid,
        resetForm,
      }) => (
        <form noValidate onSubmit={handleSubmit}>
          <TextField
            autoFocus
            error={Boolean(touched.code && errors.code)}
            fullWidth
            helperText={touched.code && errors.code}
            label={useRecoveryCode ? "Recovery code" : "6-digit code"}
            margin="normal"
            name="code"
            onBlur={handleBlur}
            onChange={handleChange}
            value={values.code}
            variant="outlined"
            autoComplete="one-time-code"
            inputProps={useRecoveryCode ? {} : { inputMode: "numeric" }}
          />
          {errors.submit && (
            <Box sx={{ mt: 3 }}>
              <FormHelperText error>{errors.submit}</FormHelperText>
            </Box>
          )}
          <Box sx={{ mt: 2 }}>
            <LoadingButton
              type="submit"
              color="primary"
              disabled={isSubmitting || !(dirty && isValid)}
              variant="contained"
              isLoading={isVerifying}
              loadingText="Verifying"
              fullWidth
              size="large"
            >
              Verify
            </LoadingButton>
          </Box>
          {allowRecoveryCode && (
            <Box sx={{ mt: 1 }}>
              <Button
                size="small"
                onClick={() => {
                  setUseRecoveryCode(!useRecoveryCode);
                  resetForm();
                }}
              >
                {useRecoveryCode
                  ? "Use authenticator app code"
                  : "Lost your device? Use a recovery code"}
              </Button>
            </Box>
          )}
        </form>
      )}
    </Formik>
  );
};

export default TwoFactorVerify;
